import { AppDispatch } from '../redux/store';
import { DraggableLocation } from 'react-beautiful-dnd';
import { getSinglePipelineDetailThunk } from '../redux/thunks/pipeline';
import { SelectChangeEvent } from '@mui/material';
import { settingSelectedPipeline } from '../redux/slices/pipeline';
import { updateDealOrderThunk } from '../redux/thunks/deal';
import { updateStageOrderThunk } from '../redux/thunks/stage';
import { DealObject, StageObject } from '../params/redux/pipeline';
import { DealOrderParams, PipelineObject, StageOrderParams } from '../params/pipeline';

export const handlePipelineChange = async (
    event: SelectChangeEvent<unknown>,
    dispatch: AppDispatch
) => {
    const pipelineId = parseInt(event.target.value as string, 10);
    try {
        await dispatch(getSinglePipelineDetailThunk(pipelineId)).unwrap();
    } catch (e) {
        console.error(e);
    }
};

export const handleStageReorder = async (
    source: DraggableLocation,
    destination: DraggableLocation,
    selectedPipeline: PipelineObject | null,
    dispatch: AppDispatch
) => {
    if (!selectedPipeline) return;
    if (!selectedPipeline.pipeline_stages) return;
    const selectedStage = selectedPipeline.pipeline_stages?.[source.index];
    if (!selectedStage) return;

    // **Clone pipeline stages correctly**
    const newStages = [...selectedPipeline.pipeline_stages];

    // Remove the dragged stage
    const [movedStage] = newStages.splice(source.index, 1);

    // Insert stage at the new index
    newStages.splice(destination.index, 0, movedStage);

    // **Update sort_order correctly**
    const updatedStages = newStages.map((stage, index) => ({
        ...stage,
        sort_order: index + 1
    }));

    // **Create a new pipeline object**
    const updatedPipeline: PipelineObject = {
        ...selectedPipeline,
        pipeline_stages: updatedStages
    };

    // **Optimistically update UI**
    dispatch(settingSelectedPipeline(updatedPipeline));

    const data: StageOrderParams = {
        stage_id: selectedPipeline.pipeline_stages?.[source.index]?.id as number,
        new_sort_order: destination.index + 1
    };

    try {
        await dispatch(updateStageOrderThunk(data)).unwrap();
    } catch (e) {
        console.error('Failed to update stage order', e);
        dispatch(settingSelectedPipeline(selectedPipeline));
    }
};

export const handleDealReorder = async (
    source: DraggableLocation,
    destination: DraggableLocation,
    selectedPipeline: PipelineObject | null,
    dispatch: AppDispatch
) => {
    if (!selectedPipeline) return;
    const sourceStageId = await parseInt(source.droppableId.split('-')[1], 10);
    const destinationStageId = await parseInt(destination.droppableId.split('-')[1], 10);

    const sourceStage = await selectedPipeline.pipeline_stages?.find((stage) => stage.id === sourceStageId);
    const destinationStage = await selectedPipeline.pipeline_stages?.find((stage) => stage.id === destinationStageId);

    if (!sourceStage || !destinationStage) return;

    const deal_id = await sourceStage.deals?.[source.index]?.id;
    if (!deal_id) return;

    const updatedPipeline = JSON.parse(JSON.stringify(selectedPipeline));
    const movedDeal = sourceStage.deals[source.index];

    updatedPipeline.pipeline_stages.forEach((stage: StageObject) => {
        if (stage.id === sourceStageId) {
            stage.deals = stage.deals.filter((deal: DealObject) => deal.id !== deal_id);
        }
        if (stage.id === destinationStageId) {
            stage.deals.splice(destination.index, 0, movedDeal);
        }
    });

    dispatch(settingSelectedPipeline(updatedPipeline));

    const data: DealOrderParams = { deal_id, stage_id: destinationStage.id, position: destination.index };
    try {
        await dispatch(updateDealOrderThunk(data)).unwrap();
    } catch (e) {
        console.error('Failed to update deal order', e);
        dispatch(settingSelectedPipeline(selectedPipeline));
    }
};

export const getTotalDeals = (pipeline: PipelineObject | null): string => {
    if (pipeline?.pipeline_stages) {
        const total = pipeline.pipeline_stages.reduce(
            (sum, stage) => sum + (stage.deals?.length || 0),
            0
        );
        return `${total} ${total === 1 ? 'Deal' : 'Deals'}`;
    }
    return '0 Deal';
};

export const getTotalValues = (pipeline: PipelineObject | null): string => {
    if (pipeline?.pipeline_stages) {
        const total = pipeline.pipeline_stages.reduce(
            (sum, stage) => sum + (stage.days_due || 0),
            0
        );
        return `${total}`;
    }
    return '0';
};